/**
 * Connectivity helpers shared by the offline layer.
 *
 * `navigator.onLine` is only half the story on a phone: it reports true in a
 * basement with one bar and no data. So callers treat BOTH "browser says
 * offline" and "the request died in the network layer" as "no service".
 */
export function isOnline(): boolean {
  return typeof navigator === 'undefined' ? true : navigator.onLine !== false
}

const NETWORK_ERROR_RE =
  /failed to fetch|load failed|networkerror|network request failed|fetch failed|network error|err_internet|err_network|timed ?out|timeout|aborterror|internet connection appears to be offline|could not connect|socket hang up|econnreset|enotfound/i

/** Gateway / upstream statuses worth retrying later instead of giving up. */
const RETRY_STATUSES = new Set([0, 408, 425, 429, 500, 502, 503, 504, 520, 521, 522, 523, 524])

/**
 * True when a failure means "the server was never reached" (or was reached
 * but is temporarily unavailable), i.e. the write should be queued and
 * retried rather than reported as a user error.
 */
export function isNetworkError(err: unknown): boolean {
  if (!isOnline()) return true
  if (!err) return false
  const e = err as { name?: string; message?: string; status?: number | string; code?: number | string; details?: string }
  if (e.name === 'AuthRetryableFetchError') return true
  const status = typeof e.status === 'string' ? Number(e.status) : e.status
  if (typeof status === 'number' && RETRY_STATUSES.has(status)) return true
  const text = `${e.name ?? ''} ${e.message ?? ''} ${e.details ?? ''}`
  return NETWORK_ERROR_RE.test(text)
}

export function isAuthError(err: unknown): boolean {
  const e = err as { status?: number; code?: number | string; message?: string }
  if (e?.status === 401 || e?.status === 403) return true
  if (e?.code === 401 || e?.code === 403 || e?.code === 'PGRST301') return true
  const msg = (e?.message || '').toLowerCase()
  return /jwt|unauthorized|not authenticated|invalid.*claim/.test(msg)
}

/** Race a request against a deadline so a hung radio can't block the queue. */
export function withTimeout<T>(p: Promise<T>, ms: number, label = 'request'): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const t = setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms)
    p.then((v) => { clearTimeout(t); resolve(v) }, (e) => { clearTimeout(t); reject(e) })
  })
}
